<template>
  <div class="map-chart" :class="className">
    <h3 class="map-chart__title">藍綠版圖之首長得票率</h3>
    <div class="map-chart__layout" v-inview="{ callback: onInview }">
      <MayorMap
        :year="year"
        :names="names"
        :colors="colors"
        v-model="selectedCountyId"/>
      <transition name="tween-info">
        <div class="map-chart__info" v-show="selectedCountyId">
          <table class="map-chart__table">
            <caption class="map-chart__table-caption">{{ selectedCountyName }}</caption>
            <thead class="map-chart__table-head">
              <tr class="map-chart__table-row"
                :style="{ background: headColor }">
                <th class="map-chart__table-col">參選人</th>
                <th class="map-chart__table-col">得票數</th>
                <th class="map-chart__table-col">得票率</th>
              </tr>
            </thead>
            <tbody class="map-chart__table-body">
              <tr class="map-chart__table-row"
                v-for="({ party, name, votes, proportion, isElected }) in countyDetail"
                :key="name"
                :class="{ 'map-chart--table-elected': isElected }"
                :style="{ background: mapTableColors[party] }">
                <template v-if="name">
                  <td class="map-chart__table-col map-chart__table-name"><!--
                  --><SvgSymbol class="map-chart__table-name-icon" :use="`#party-${party}`"/><!--
                  --><span class="map-chart__table-name-text">{{ name }}</span>
                  </td>
                  <td class="map-chart__table-col map-chart__table-votes">{{ votes | numeric }}</td>
                  <td class="map-chart__table-col map-chart__table-proportion">{{ proportion | percentage }}</td>
                </template>
                <template v-else>
                  <td class="map-chart__table-col map-chart__table-name" colspan="3"><!--
                  --><SvgSymbol class="map-chart__table-name-icon" :use="`#party-${party}`"/><!--
                  --><span class="map-chart__table-name-text">{{ partyNames[party] }}未推派候選人</span>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </transition>
      <div class="map-chart__ui">
        <select class="map-chart__ui-select" v-model="selectedCountyId">
          <option value="">請選擇縣市</option>
          <option v-for="(name, index) in selectNames"
            :key="ids[index]"
            :value="ids[index]"
            :title="name">{{ name }}</option>
        </select>
        <transition name="tween-ui-back">
          <button class="map-chart__ui-back"
            @click.prevent.stop="onBackClick"
            v-show="selectedCountyId">返回</button>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { MAP_TABLE_COLORS, PARTY_NAMES } from '@/constant'
import MayorMap from '@/components/MayorMap'

export default {
  name: 'map-chart',
  components: {
    MayorMap
  },
  props: {
    year: { type: Number, required: true },
    ids: { type: Array, default: () => [] },
    names: { type: Array, default: () => [] },
    colors: { type: Array, default: () => [] },
    detail: { type: Object, default: () => {} },
    headColor: { type: String, default: '#a1a1a1' }
  },
  data () {
    return {
      isInview: false,
      mapTableColors: MAP_TABLE_COLORS,
      partyNames: PARTY_NAMES,
      selectedCountyId: '',
      lastSelectedCountyId: ''
    }
  },
  computed: {
    className () {
      return {
        'map-chart--inview': this.isInview
      }
    },
    selectNames () {
      if (this.year !== 2010) {
        return this.names
      }
      return this.names.map(name => (name === '桃園市' ? '桃園縣' : name))
    },
    selectedCountyName () {
      if (!this.countyDetail || !this.countyDetail.length) {
        return ''
      }
      return this.countyDetail[0].county
    },
    countyDetail () {
      return this.detail[this.lastSelectedCountyId] || null
    }
  },
  watch: {
    selectedCountyId (value, lastValue) {
      if (!value) {
        return
      }
      this.lastSelectedCountyId = value
      this.$ga('newmedia.send', {
        'hitType': 'event',
        'eventCategory': 'Read',
        'eventAction': 'Click',
        'eventLabel': 'CityVotes'
      })
    }
  },
  methods: {
    onInview () {
      this.isInview = true
    },
    onBackClick () {
      this.selectedCountyId = ''
    }
  }
}
</script>

<style lang="scss">
.map-chart {
  $comp: &;
  position: relative;
  margin-top: 50px;
  opacity: 0;
  overflow: hidden;
  transform: translateY(10px);
  transition:
    opacity 0.5s ease 0.3s,
    transform 0.7s ease 0.3s;

  &__title {
    font-size: 1.125em;
    font-weight: bold;
    text-align: center;
    @include rwd($RWD_TABLET_SMALL) {
      font-size: 1.25em;
    }
    @include rwd($RWD_TABLET) {
      font-size: 1.5em;
    }
    @include rwd($RWD_TABLET) {
      font-size: 1.5em;
    }
  }

  &__layout {
    position: relative;
    margin-top: 20px;
    @include rwd($RWD_TABLET_SMALL) {
      margin-top: 25px;
    }
  }

  &__info {
    position: absolute;
    bottom: 10px;
    left: 10px;
    right: 10px;
    border-radius: 2px;
    overflow: hidden;
    backface-visibility: hidden;
  }

  &__table {
    position: relative;
    width: 100%;

    &-caption {
      padding: 4px 0;
      font-size: 1em;
      @include rwd($RWD_TABLET_SMALL) {
        padding: 8px 0;
        font-size: 1.125em;
      }
      @include rwd($RWD_TABLET) {
        padding: 12px 0;
        font-size: 1.25em;
      }
    }

    &-head {
      #{$comp}__table-col {
        font-size: 0.8125em;
        font-weight: 500;
        @include rwd($RWD_TABLET_SMALL) {
          font-size: 0.9375em;
        }
        @include rwd($RWD_TABLET) {
          font-size: 1em;
        }
      }
    }

    &-body {
      #{$comp}__table-col {
        font-size: 1em;
        @include rwd($RWD_TABLET_SMALL) {
          font-size: 1.125em;
        }
        @include rwd($RWD_TABLET) {
          font-size: 1.25em;
        }
      }
    }

    &-row {
      background: white;
    }

    &-col {
      width: 33.33%;
      padding: 10px 0;
      text-align: center;
      line-height: 1em;
      border: 1px solid white;
    }

    &-name {
      text-align: left;
      text-indent: 8px;
      white-space: nowrap;
      @include rwd($RWD_TABLET_SMALL) {
        text-indent: 25px;
      }
      @include rwd($RWD_TABLET_SMALL) {
        text-indent: 20px;
      }

      &-icon.svg-symbol {
        display: inline-block;
        width: 20px;
        height: 20px;
        vertical-align: middle;
        @include rwd($RWD_TABLET) {
          width: 24px;
          height: 24px;
        }
      }

      &-text {
        margin-left: 5px;
        vertical-align: middle;
      }
      @include rwd($RWD_TABLET_SMALL) {
        margin-left: 10px;
      }
      @include rwd($RWD_TABLET) {
        margin-left: 15px;
      }
    }
  }

  &__ui {
    position: absolute;
    top: 10px;
    left: 10px;
    height: 30px;

    &-select {
      height: 100%;
    }

    &-back {
      position: relative;
      margin-left: 10px;
      padding: 5px 10px;
      color: white;
      background: #1a1a1a;
      border-radius: 3px;
      cursor: pointer;
      &:active {
        transform: translateY(1px);
      }
    }
  }

  &--inview {
    opacity: 1;
    transform: translateY(0);
  }

  // vue transitions
  .tween-info {
    &-enter-active,
    &-leave-active {
      transition:
        opacity 0.4s,
        transform 0.4s;
    }
    // &-leave-active {
    //   transition: none;
    // }

    &-enter,
    &-leave-to {
      opacity: 0;
      transform: translateY(100%);
    }

    &-enter-to,
    &-leave {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .tween-ui-back {
    &-enter-active,
    &-leave-active {
      transition:
        opacity 0.3s,
        transform 0.3s;
    }
    // &-leave-active {
    //   transition: none;
    // }

    &-enter,
    &-leave-to {
      opacity: 0;
      transform: translateX(10px);
    }

    &-enter-to,
    &-leave {
      opacity: 1;
      transform: translateX(0);
    }
  }
}
</style>
